import { ButtonType, ButtonWidget, TabStyles, TextLinkWidget } from '@tableau/widgets';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { style } from 'typestyle';
import { SessionServerSelectors, SessionUserSelectors } from '../../../../app/selectors/SessionInfoSelectors';
import { Messages } from '../../../../codegen/Localize';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { UserSettingsActions } from '../../../../userSettings/services/UserSettingsActions';
import { SettingsSection } from '../../../components/common/SettingsSection';
import { useSelector } from '../../utils';
import { serverSettingsForSiteAdmin, SiteSettingsForSiteAdminProps } from '../ReactSiteSettingsForSiteAdmin';
import { componentRegistryEntry } from '../SettingsComponentRegistryFunctions';

export type StartPageSectionProps = SiteSettingsForSiteAdminProps<'siteStartPageUri'> & serverSettingsForSiteAdmin;

const spacerStyle = style({
  paddingBottom: TabStyles.Spacing.MediumSpace
});

export function StartPageSection(props: StartPageSectionProps) {
  const [ siteStartPageUriState, setSiteStartPageUriState ] = useState<string>(props.settings.siteStartPageUri);
  const isServerAdmin = useSelector(SessionUserSelectors.isSessionServerAdmin);

  const openResetStartPageDialog = useCallback(() => {
    UserSettingsActions.confirmResetSiteDefaultStartPage(
      () => {
        setSiteStartPageUriState(null);
      },
      isServerAdmin ? props.siteId : undefined
    );
  }, [setSiteStartPageUriState]);

  const isOnlineEnvironment = useSelector(SessionServerSelectors.isOnlineEnvironment);

  const showSiteStartPage = useShowSiteStartPage(isOnlineEnvironment);

  return !showSiteStartPage ? null : (
    <SettingsSection title={Messages.settings_startPage_title()} description={getDescription(isOnlineEnvironment)} testId={'reset-default-site-start-page-setting'}>
      <div className={spacerStyle} id='current-start-page-id'>
        <span>{Messages.settings_startPage_current_start_page()}</span>
        <TextLinkWidget
          href={`/#${getStartPageUrl(siteStartPageUriState, props)}`}
          children={getStartPageUrl(siteStartPageUriState, props)}
          testId={'default-start-page-link'}
          target='_blank'
        />
        {!siteStartPageUriState &&
        <span className='tb-disabled' data-tb-test-id={'default-start-page-aside'}>
          &nbsp;{Messages.settings_startPage_system_default_aside()}
        </span>}
      </div>
      <ButtonWidget
        buttonType={ButtonType.Outline}
        testId='reset-default-site-start-page'
        disabled={!siteStartPageUriState}
        handleClick={openResetStartPageDialog}
        children={Messages.settings_startPage_reset_button()}
      />
    </SettingsSection>
  );
}

function useShowSiteStartPage(isOnlineEnvironment: boolean): boolean {
  const isSessionMultiSite = useSelector<boolean>(SessionUserSelectors.isSessionMultiSite);
  return FeatureFlags.siteDefaultStartPage && (isSessionMultiSite || isOnlineEnvironment);
}

function getStartPageUrl(siteStartPage: string, props: StartPageSectionProps): string {
  return siteStartPage || props.serverSettings?.defaultStartPage || '/';
}

function getDescription(isOnlineEnvironment: boolean) {
  return isOnlineEnvironment
    ? Messages.settings_siteStartPage_description_online()
    : Messages.settings_siteStartPage_description_server();
}

/* for registering the component in settings registry */
export const StartPageSpec = componentRegistryEntry({
  name: 'StartPageSection',
  component: StartPageSection
});

